<template>
  <page-bar title="Week plan" />
  <v-container>
    <div class="d-flex align-center flex-wrap ga-2 mb-2">
      <div class="d-flex align-center">
        <v-btn
          icon="mdi-chevron-left"
          variant="text"
          aria-label="Previous week"
          @click="$emit('previous-week')"
        />
        <h1 class="text-title-large mx-1">{{ weekTitle }}</h1>
        <v-btn
          icon="mdi-chevron-right"
          variant="text"
          aria-label="Next week"
          @click="$emit('next-week')"
        />
        <v-btn v-if="!isCurrentWeek" variant="text" @click="$emit('this-week')">
          This week
        </v-btn>
      </div>
      <v-spacer />
      <v-btn
        prepend-icon="mdi-creation"
        variant="tonal"
        @click="planDialogOpen = true"
      >
        Plan my week
      </v-btn>
      <v-btn
        prepend-icon="mdi-content-copy"
        variant="tonal"
        :loading="copying"
        @click="$emit('copy-last-week')"
      >
        Copy last week
      </v-btn>
      <v-btn
        prepend-icon="mdi-cart-arrow-down"
        variant="tonal"
        @click="shoppingListOpen = true"
      >
        Grocery list
      </v-btn>
    </div>
    <!-- Columns follow the room the page has: one day per column on wide
         screens, so the whole week reads left to right -->
    <div class="week">
      <div class="week-grid">
        <v-card v-for="day in orderedDays" :key="day">
          <v-img
            v-if="dayImage(day)"
            :src="dayImage(day)"
            alt=""
            height="120"
            cover
          />
          <v-card-title class="text-capitalize">{{ day }}</v-card-title>
          <v-card-text>
            <div
              v-for="slot in shownSlots(day)"
              :key="slot"
              class="mb-2"
              :data-slot="slot"
            >
              <div
                v-if="shownSlots(day).length > 1"
                class="text-body-small text-capitalize"
              >
                {{ slot }}
              </div>
              <template v-if="mealAt(day, slot)">
                <div class="d-flex align-center flex-wrap ga-1">
                  <v-chip
                    class="meal"
                    closable
                    :close-label="`Remove ${day} ${slot}`"
                    :prepend-icon="
                      mealAt(day, slot)!.recipeId ? 'mdi-food' : 'mdi-pencil'
                    "
                    @click:close="$emit('remove-meal', day, slot)"
                  >
                    {{ mealAt(day, slot)!.title }}
                  </v-chip>
                  <v-btn
                    icon="mdi-swap-horizontal"
                    variant="text"
                    :aria-label="`Change ${day} ${slot}`"
                    @click="openPicker(day, slot)"
                  />
                </div>
                <servings-selector
                  v-if="mealAt(day, slot)!.recipeId"
                  :model-value="servingsFor(mealAt(day, slot)!)"
                  @update:model-value="
                    (servings: number) =>
                      $emit('set-meal', day, slot, {
                        ...mealAt(day, slot)!,
                        servings,
                      })
                  "
                />
              </template>
              <v-btn
                v-else
                variant="tonal"
                prepend-icon="mdi-plus"
                @click="openPicker(day, slot)"
              >
                Add meal
              </v-btn>
            </div>
          </v-card-text>
          <!-- Dinner is always shown; breakfast and lunch once added -->
          <v-card-actions v-if="addableSlots(day).length">
            <v-btn
              v-for="slot in addableSlots(day)"
              :key="slot"
              variant="text"
              prepend-icon="mdi-plus"
              class="text-capitalize"
              :aria-label="`Add ${slot} on ${day}`"
              @click="openPicker(day, slot)"
            >
              {{ slot }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </div>
    <shopping-list-dialog
      :open="shoppingListOpen"
      :lines="shoppingList.lines"
      :unlisted="shoppingList.unlisted"
      :adding="addingToGroceries"
      :unit-system="unitSystem"
      @add="addToGroceries"
      @close="shoppingListOpen = false"
    />
    <v-snackbar v-model="addedToGroceries" timeout="6000">
      Added {{ addedCount }} {{ addedCount === 1 ? 'item' : 'items' }} to the
      grocery list
      <template v-slot:actions>
        <v-btn variant="text" @click="undoAddToGroceries"> Undo </v-btn>
      </template>
    </v-snackbar>
    <v-snackbar v-model="copiedShown" timeout="4000">
      {{ copiedMessage }}
    </v-snackbar>
    <plan-week-dialog
      :open="planDialogOpen"
      :plan="plan"
      :ordered-days="orderedDays"
      :recipes="recipes"
      :planning="planning"
      :proposal="proposal"
      :error="planError"
      @plan="planWeek"
      @accept="acceptProposal"
      @reset="resetProposal"
      @close="closePlanDialog"
    />
    <meal-picker-dialog
      :open="pickerOpen"
      :day-label="pickerLabel"
      :recipes="recipes"
      :suggestions="suggestions"
      @select="onMealSelected"
      @close="pickerOpen = false"
    />
  </v-container>
</template>

<script lang="ts">
  import { computed, defineComponent, type PropType, ref, watch } from 'vue';
  import {
    DAYS,
    type Day,
    type MealEntry,
    SLOTS,
    type Slot,
    type WeekPlan,
  } from '@/service/weekplan/types';
  import type { Recipe } from '@/service/recipe/types';
  import { DEFAULT_SERVINGS } from '@/service/recipe/types';
  import { servingsOf } from '@/service/recipe/scaling';
  import MealPickerDialog from '@/components/weekplan/MealPickerDialog.vue';
  import ShoppingListDialog from '@/components/weekplan/ShoppingListDialog.vue';
  import PlanWeekDialog from '@/components/weekplan/PlanWeekDialog.vue';
  import {
    type PlanRequest,
    planErrorMessage,
    type Proposal,
    type ProposedMeals,
    type WeekPlannerInterface,
  } from '@/service/weekplan/weekPlanner';
  import {
    collectShoppingList,
    type ShoppingLine,
  } from '@/service/weekplan/shoppingList';
  import type { GroceryClientInterface } from '@/service/groceries/groceryClient';
  import {
    type GroceryPlan,
    planAdditions,
  } from '@/service/groceries/planAdditions';
  import { weekLabel } from '@/service/weekplan/history';
  import type { UnitSystem } from '@/service/recipe/units';
  import ServingsSelector from '@/components/recipe/servings-selector/ServingsSelector.vue';
  import PageBar from '@/components/infrastructure/page-bar/PageBar.vue';

  export default defineComponent({
    name: 'WeekPlanView',
    components: {
      MealPickerDialog,
      PageBar,
      PlanWeekDialog,
      ServingsSelector,
      ShoppingListDialog,
    },
    props: {
      plan: {
        type: Object as PropType<WeekPlan>,
        required: true,
      },
      recipes: {
        type: Array as PropType<Recipe[]>,
        default: () => [],
      },
      weekStartDay: {
        type: String as PropType<Day>,
        default: 'monday',
      },
      groceryClient: {
        type: Object as PropType<GroceryClientInterface>,
        required: true,
      },
      // Proposes meals for "Plan my week"
      weekPlanner: {
        type: Object as PropType<WeekPlannerInterface>,
        required: true,
      },
      unitSystem: {
        type: String as PropType<UnitSystem>,
        default: 'original',
      },
      isCurrentWeek: {
        type: Boolean,
        default: true,
      },
      // Recipes offered first in the meal picker
      suggestions: {
        type: Array as PropType<Recipe[]>,
        default: () => [],
      },
      copying: {
        type: Boolean,
        default: false,
      },
      // A new object after each "Copy last week", to show the outcome
      copyResult: {
        type: Object as PropType<{ copied: number }>,
        default: undefined,
      },
      // A new object after accepted meals are added, to show the outcome
      addResult: {
        type: Object as PropType<{ added: number }>,
        default: undefined,
      },
    },
    emits: {
      'set-meal': (_day: Day, _slot: Slot, _entry: MealEntry) => true,
      'remove-meal': (_day: Day, _slot: Slot) => true,
      'previous-week': () => true,
      'next-week': () => true,
      'this-week': () => true,
      'copy-last-week': () => true,
      'add-meals': (_meals: ProposedMeals) => true,
    },
    setup(props, { emit }) {
      const weekTitle = computed(() =>
        weekLabel(props.plan.weekStart, props.weekStartDay),
      );

      const copiedShown = ref(false);
      const copiedMessage = ref('');
      watch(
        () => props.copyResult,
        (result) => {
          if (!result) return;
          const { copied } = result;
          copiedMessage.value = copied
            ? `Copied ${copied} ${copied === 1 ? 'meal' : 'meals'} from last week`
            : 'Nothing to copy from last week';
          copiedShown.value = true;
        },
      );

      watch(
        () => props.addResult,
        (result) => {
          if (!result) return;
          const { added } = result;
          copiedMessage.value = added
            ? `Added ${added} ${added === 1 ? 'meal' : 'meals'} to your week`
            : 'Those meals are already planned';
          copiedShown.value = true;
        },
      );

      const planDialogOpen = ref(false);
      const planning = ref(false);
      const proposal = ref<Proposal>();
      const planError = ref('');

      const pickerOpen = ref(false);
      const pickerDay = ref<Day>('monday');
      const pickerSlot = ref<Slot>('dinner');

      const orderedDays = computed(() => {
        const startIndex = DAYS.indexOf(props.weekStartDay);
        return [...DAYS.slice(startIndex), ...DAYS.slice(0, startIndex)];
      });

      const pickerLabel = computed(() => {
        const day = pickerDay.value;
        return `${day.charAt(0).toUpperCase()}${day.slice(1)} ${pickerSlot.value}`;
      });

      const shoppingListOpen = ref(false);
      const shoppingList = computed(() =>
        collectShoppingList(props.plan, props.recipes, orderedDays.value),
      );
      const addingToGroceries = ref(false);
      const addedToGroceries = ref(false);
      const addedCount = ref(0);
      let lastAdded: GroceryPlan | undefined;

      function mealAt(day: Day, slot: Slot): MealEntry | undefined {
        return props.plan.meals[day]?.[slot];
      }

      function recipeOf(recipeId?: string): Recipe | undefined {
        return recipeId
          ? props.recipes.find((recipe) => recipe.id === recipeId)
          : undefined;
      }

      return {
        weekTitle,
        copiedShown,
        copiedMessage,
        orderedDays,
        shoppingListOpen,
        shoppingList,
        addingToGroceries,
        addedToGroceries,
        addedCount,
        // Merged into the grocery list like a single recipe's ingredients
        async addToGroceries(lines: ShoppingLine[]) {
          addingToGroceries.value = true;
          const plan = planAdditions(
            await props.groceryClient.get(),
            lines.map(({ text, amount, unit, recipeId }) => ({
              text,
              amount,
              unit,
              recipeId,
            })),
          );
          await props.groceryClient.apply(plan);
          lastAdded = plan;
          addedCount.value = lines.length;
          addingToGroceries.value = false;
          shoppingListOpen.value = false;
          addedToGroceries.value = true;
        },
        async undoAddToGroceries() {
          addedToGroceries.value = false;
          if (lastAdded) {
            await props.groceryClient.revert(lastAdded);
            lastAdded = undefined;
          }
        },
        planDialogOpen,
        planning,
        proposal,
        planError,
        async planWeek(request: PlanRequest) {
          planning.value = true;
          planError.value = '';
          try {
            proposal.value = await props.weekPlanner.plan(request);
          } catch (error) {
            planError.value = planErrorMessage(error);
          } finally {
            planning.value = false;
          }
        },
        acceptProposal(meals: ProposedMeals) {
          emit('add-meals', meals);
          planDialogOpen.value = false;
        },
        resetProposal() {
          proposal.value = undefined;
          planError.value = '';
        },
        // Opens on the choices again next time
        closePlanDialog() {
          planDialogOpen.value = false;
          proposal.value = undefined;
          planError.value = '';
        },
        pickerOpen,
        pickerLabel,
        mealAt,
        shownSlots(day: Day): Slot[] {
          return SLOTS.filter((slot) => slot === 'dinner' || mealAt(day, slot));
        },
        addableSlots(day: Day): Slot[] {
          return SLOTS.filter(
            (slot) => slot !== 'dinner' && !mealAt(day, slot),
          );
        },
        // The picture of the day's dinner, else of another meal
        dayImage(day: Day): string | undefined {
          for (const slot of ['dinner', 'lunch', 'breakfast'] as const) {
            const image = recipeOf(mealAt(day, slot)?.recipeId)?.image;
            if (image) return image;
          }
          return undefined;
        },
        servingsFor(meal: MealEntry): number {
          const recipe = recipeOf(meal.recipeId);
          return (
            meal.servings ?? (recipe ? servingsOf(recipe) : DEFAULT_SERVINGS)
          );
        },
        openPicker(day: Day, slot: Slot) {
          pickerDay.value = day;
          pickerSlot.value = slot;
          pickerOpen.value = true;
        },
        onMealSelected(entry: MealEntry) {
          emit('set-meal', pickerDay.value, pickerSlot.value, entry);
          pickerOpen.value = false;
        },
      };
    },
  });
</script>

<style scoped>
  .week {
    container-type: inline-size;
  }

  .week-grid {
    display: grid;
    gap: 16px;
    grid-template-columns: minmax(0, 1fr);
    align-items: start;
  }

  @container (min-width: 560px) {
    .week-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @container (min-width: 880px) {
    .week-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @container (min-width: 1500px) {
    .week-grid {
      grid-template-columns: repeat(7, minmax(0, 1fr));
    }
  }

  /* The week uses the whole width; Vuetify caps containers at 1400px */
  .v-container {
    max-width: none;
  }

  /* Long meal names wrap instead of being cut off */
  .meal {
    height: auto;
    min-height: 32px;
    white-space: normal;
  }
</style>
